import { useCallback } from 'react';
import { useDrag, useDrop } from 'react-dnd';

import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

import type { ItemType } from './types/item-type.type';

interface Props<D extends object> {
  readonly disableMoveDown: boolean;
  readonly disableMoveUp: boolean;
  readonly dragItem: D;
  readonly isDropAfter: (draggedItem: D, targetItem: D) => boolean;
  readonly label: string;
  readonly onDrop: (draggedItem: D) => void;
  readonly onMoveDown: () => void;
  readonly onMoveUp: () => void;
  readonly type: ItemType;
}

const DROP_INDICATOR_WIDTH_PX = 3;

// The card does not move while dragging: a primary border shows where the
// dragged card will be inserted, and the move happens on drop. The arrow
// buttons give the same moves without drag and drop.
export const ReorderableCard = <D extends object>({
  disableMoveDown,
  disableMoveUp,
  dragItem,
  isDropAfter,
  label,
  onDrop,
  onMoveDown,
  onMoveUp,
  type,
}: Readonly<Props<D>>) => {
  const [{ isDragging }, dragRef] = useDrag(
    () => ({
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
      item: dragItem,
      type,
    }),
    [type, dragItem],
  );

  const [{ dropIndicator }, dropRef] = useDrop(
    () => ({
      accept: type,
      collect: (monitor) => {
        const draggedItem = monitor.getItem<D | null>();

        if (!draggedItem || !monitor.isOver({ shallow: true })) {
          return { dropIndicator: null };
        }

        return {
          dropIndicator: isDropAfter(draggedItem, dragItem)
            ? ('after' as const)
            : ('before' as const),
        };
      },
      drop: (draggedItem: D) => {
        onDrop(draggedItem);
      },
    }),
    [dragItem, isDropAfter, onDrop, type],
  );

  const setRef = useCallback(
    (node: HTMLDivElement | null) => {
      dragRef(node);
      dropRef(node);
    },
    [dragRef, dropRef],
  );

  return (
    <Paper
      ref={setRef}
      sx={{
        alignItems: 'center',
        borderBottomColor:
          dropIndicator === 'after' ? 'primary.main' : undefined,
        borderBottomWidth:
          dropIndicator === 'after' ? DROP_INDICATOR_WIDTH_PX : undefined,
        borderTopColor: dropIndicator === 'before' ? 'primary.main' : undefined,
        borderTopWidth:
          dropIndicator === 'before' ? DROP_INDICATOR_WIDTH_PX : undefined,
        cursor: 'move',
        display: 'flex',
        opacity: isDragging ? 0.5 : 1,
        p: 1.5,
      }}
      variant="outlined"
    >
      <DragIndicatorIcon color="action" sx={{ mr: 1 }} />
      <Typography sx={{ flex: 1 }} variant="body2">
        {label}
      </Typography>
      <IconButton
        aria-label={`Monter ${label}`}
        disabled={disableMoveUp}
        onClick={onMoveUp}
        size="small"
      >
        <ArrowUpwardIcon fontSize="small" />
      </IconButton>
      <IconButton
        aria-label={`Descendre ${label}`}
        disabled={disableMoveDown}
        onClick={onMoveDown}
        size="small"
      >
        <ArrowDownwardIcon fontSize="small" />
      </IconButton>
    </Paper>
  );
};
